// تب Entitlement جزئیات مستأجر (SCR-CONSOLE-002؛ US-PLT-033): Featureهای مؤثر، مصرف Limitها و استثناهای زمان‌دار با افزودن و حذف.
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ConsoleTenantDetail,
  type OverrideRequest,
  type OverrideView,
} from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ReasonDialog,
  SelectRHF,
  TextFieldRHF,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { usePlatformAccess } from '../../access';
import { consoleKeys, toNumber } from '../../api';
import { ALL_FEATURES, LIMIT_KEYS, limitLabelKey, REGULATORY_FEATURES } from '../../catalog';
import { startOfBusinessDayIso } from '../../dates';

const overrideSchema = z
  .object({
    kind: z.enum(['feature', 'limit']),
    featureKey: z.string(),
    enabled: z.boolean(),
    limitKey: z.string(),
    limitValue: z.string(),
    reason: z.string().trim().min(3, { error: 'console:validation.reason_min' }).max(500, {
      error: 'common:validation.too_long',
    }),
    expiresAt: z.string().nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'feature' && !value.featureKey) {
      ctx.addIssue({ code: 'custom', path: ['featureKey'], message: 'common:validation.required' });
    }
    if (value.kind === 'limit') {
      if (!value.limitKey) {
        ctx.addIssue({ code: 'custom', path: ['limitKey'], message: 'common:validation.required' });
      }
      if (!/^\d{1,9}$/.test(value.limitValue.trim())) {
        ctx.addIssue({
          code: 'custom',
          path: ['limitValue'],
          message: 'console:validation.non_negative_integer',
        });
      }
    }
  });

type OverrideForm = z.input<typeof overrideSchema>;

function OverrideDialog({
  tenantId,
  open,
  onClose,
}: {
  tenantId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError, reset } = useForm<OverrideForm>({
    resolver: zodResolver(overrideSchema),
    defaultValues: {
      kind: 'feature',
      featureKey: '',
      enabled: true,
      limitKey: '',
      limitValue: '',
      reason: '',
      expiresAt: null,
    },
  });
  const kind = useWatch({ control, name: 'kind' });

  const add = useMutation({
    mutationFn: (body: OverrideRequest) =>
      unwrap(
        api.POST('/api/v1/platform/console/tenants/{id}/entitlement-overrides', {
          params: { path: { id: tenantId } },
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: consoleKeys.overrides(tenantId) });
      void queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(tenantId) });
      reset();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['featureKey', 'enabled', 'limitKey', 'limitValue', 'reason', 'expiresAt'],
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = handleSubmit((values) =>
    add.mutate({
      featureKey: values.kind === 'feature' ? values.featureKey : null,
      enabled: values.kind === 'feature' ? values.enabled : null,
      limitKey: values.kind === 'limit' ? values.limitKey : null,
      limitValue: values.kind === 'limit' ? Number(values.limitValue) : null,
      reason: values.reason.trim(),
      expiresAt: values.expiresAt ? startOfBusinessDayIso(values.expiresAt) : null,
    }),
  );

  return (
    <FormDialog
      open={open}
      titleKey="console:entitlements.override_dialog.title"
      onSubmit={() => void submit()}
      onClose={() => {
        reset();
        setSubmitError(undefined);
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={add.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={control}
        name="kind"
        labelKey="console:entitlements.override_dialog.kind"
        fullWidth
        options={[
          { value: 'feature', labelKey: 'console:entitlements.override_dialog.kind_feature' },
          { value: 'limit', labelKey: 'console:entitlements.override_dialog.kind_limit' },
        ]}
      />
      {kind === 'feature' ? (
        <>
          <SelectRHF
            control={control}
            name="featureKey"
            labelKey="console:entitlements.override_dialog.feature"
            required
            fullWidth
            options={ALL_FEATURES.map((feature) => ({ value: feature, label: feature }))}
          />
          <CheckboxRHF
            control={control}
            name="enabled"
            labelKey="console:entitlements.override_dialog.enabled"
          />
        </>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <SelectRHF
            control={control}
            name="limitKey"
            labelKey="console:entitlements.override_dialog.limit"
            required
            fullWidth
            options={LIMIT_KEYS.map((key) => ({ value: key, labelKey: limitLabelKey(key) }))}
          />
          <TextFieldRHF
            control={control}
            name="limitValue"
            labelKey="console:entitlements.override_dialog.limit_value"
            required
            fullWidth
            slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr' } }}
          />
        </Stack>
      )}
      <TextFieldRHF
        control={control}
        name="reason"
        labelKey="console:entitlements.override_dialog.reason"
        required
        multiline
        minRows={2}
        fullWidth
      />
      <JalaliDatePickerRHF
        control={control}
        name="expiresAt"
        labelKey="console:entitlements.override_dialog.expires_at"
        helperKey="console:entitlements.override_dialog.expires_help"
      />
    </FormDialog>
  );
}

export function EntitlementsTab({ tenant }: { tenant: ConsoleTenantDetail }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const queryClient = useQueryClient();
  const canOverride = access.can('platform.entitlement.override');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<OverrideView | null>(null);

  const overrides = useQuery({
    queryKey: consoleKeys.overrides(tenant.id),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/platform/console/tenants/{id}/entitlement-overrides', {
          params: { path: { id: tenant.id } },
        }),
      ),
    enabled: canOverride,
  });

  const remove = useMutation({
    mutationFn: ({ overrideId, reason }: { overrideId: string; reason: string }) =>
      unwrap(
        api.DELETE('/api/v1/platform/console/tenants/{id}/entitlement-overrides/{overrideId}', {
          params: { path: { id: tenant.id, overrideId }, query: { reason } },
        }),
      ),
    onSuccess: () => {
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: consoleKeys.overrides(tenant.id) });
      void queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(tenant.id) });
    },
  });

  const enabled = new Set(tenant.enabledFeatures);
  const columns: DataTableColumn<OverrideView>[] = [
    {
      id: 'key',
      headerKey: 'console:entitlements.overrides.columns.key',
      cell: (row) => <bdi dir="ltr">{row.featureKey ?? row.limitKey}</bdi>,
    },
    {
      id: 'value',
      headerKey: 'console:entitlements.overrides.columns.value',
      cell: (row) =>
        row.featureKey
          ? t(row.enabled ? 'console:entitlements.on' : 'console:entitlements.off')
          : faDigits(toNumber(row.limitValue) ?? 0),
    },
    { id: 'reason', headerKey: 'console:entitlements.overrides.columns.reason', cell: (row) => row.reason },
    {
      id: 'expiresAt',
      headerKey: 'console:entitlements.overrides.columns.expires_at',
      cell: (row) =>
        row.expiresAt ? <JalaliDate value={row.expiresAt} withTime /> : t('console:entitlements.no_expiry'),
    },
    {
      id: 'actions',
      headerKey: 'console:entitlements.overrides.columns.actions',
      cell: (row) => (
        <Tooltip title={t('console:entitlements.overrides.remove')}>
          <IconButton
            aria-label={t('console:entitlements.overrides.remove')}
            onClick={() => setRemoving(row)}
            data-testid={`override-remove-${row.id}`}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Stack spacing={2} data-testid="entitlements-tab">
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h3" component="h2" gutterBottom>
          {t('console:entitlements.features_title')}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
          data-testid="effective-features"
        >
          {ALL_FEATURES.map((feature) => (
            <Chip
              key={feature}
              label={<bdi dir="ltr">{feature}</bdi>}
              color={enabled.has(feature) ? 'success' : 'default'}
              variant={enabled.has(feature) ? 'filled' : 'outlined'}
              data-enabled={enabled.has(feature) ? 'true' : 'false'}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {t('console:entitlements.regulatory_note', { features: REGULATORY_FEATURES.join(' · ') })}
        </Typography>
      </Paper>

      {canOverride && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="h3" component="h2" sx={{ flex: 1 }}>
              {t('console:entitlements.overrides.title')}
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setAdding(true)}
              data-testid="override-add"
            >
              {t('console:entitlements.overrides.add')}
            </Button>
          </Stack>
          <DataTable
            captionKey="console:entitlements.overrides.title"
            columns={columns}
            rows={overrides.data?.items}
            getRowId={(row) => row.id}
            isLoading={overrides.isPending}
            error={overrides.error}
            onRetry={() => void overrides.refetch()}
            emptyTitleKey="console:entitlements.overrides.empty"
          />
          <OverrideDialog tenantId={tenant.id} open={adding} onClose={() => setAdding(false)} />
          <ReasonDialog
            open={removing !== null}
            titleKey="console:entitlements.overrides.remove_title"
            messageKey="console:entitlements.overrides.remove_message"
            confirmKey="console:entitlements.overrides.remove"
            busy={remove.isPending}
            error={remove.error}
            onCancel={() => {
              remove.reset();
              setRemoving(null);
            }}
            onConfirm={(reason) => removing && remove.mutate({ overrideId: removing.id, reason })}
          />
        </Paper>
      )}
    </Stack>
  );
}
